import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { isEmpty, sortBy } from 'lodash';
import { array, object } from 'prop-types';
import { Button, CircularProgress, Paper } from '@material-ui/core';
import MaterialTable from 'material-table';
import AutoComplete from '../../components/FormFields/Autocomplete';
import CancelButton from '../../components/CancelButton';
import ConfirmButton from '../../components/ConfirmButton';

import classes from './ServiceCallStatusMappingTable.module.css';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as ArchitectActions from '../../actions/architectActions';

const ServiceCallStatusMappingTable = ({
    createErpServiceCallMapping,
    deleteStatusMapping,
    enqueueSnackbar,
    erpAgents,
    erpAgentsLoading,
    existingErpSAStatusMapping,
    existingErpSCStatusMapping,
    fetchErpServiceApptMapping,
    fetchErpServiceCallMapping,
    fetchServiceAppointmentStatuses,
    fetchServiceCallStatuses,
    selectedErpAgentId,
    selectedErpAgentStatuses,
    serviceAppointmentStatusesList,
    serviceCallStatusesList,
    toggleLoading,
    updateErpServiceCallMapping,
    ...props
}) => {
    let content;

    const [isLoading, setIsLoading] = useState(false);

    const [serviceCallSelectState, setServiceCallSelectState] = useState({});
    const [serviceAppointmentSelectState, setServiceAppointmentSelectState] = useState({});

    const getMapping = async () => {
        setIsLoading(true);

        await fetchErpServiceCallMapping(selectedErpAgentId);
        await fetchErpServiceApptMapping(selectedErpAgentId);

        setIsLoading(false);
    };

    useEffect(() => {
        setServiceCallSelectState({});
        setServiceAppointmentSelectState({});

        if (selectedErpAgentId) {
            getMapping();
        }
    }, [selectedErpAgentId]);

    useEffect(() => {
        if (!isEmpty(existingErpSCStatusMapping) && isEmpty(serviceCallSelectState)) {
            const state = {};

            existingErpSCStatusMapping.forEach(mapping => {
                const { externalId, entityId } = mapping;

                let selectItem;

                serviceCallStatusesList.forEach(status => {
                    if (status.id === entityId) {
                        selectItem = status;
                    }
                });

                state[externalId] = { ...selectItem };
            });

            setServiceCallSelectState({ ...serviceCallSelectState, ...state });
        }
    }, [existingErpSCStatusMapping]);

    useEffect(() => {
        if (!isEmpty(existingErpSAStatusMapping) && isEmpty(serviceAppointmentSelectState)) {
            const state = {};

            existingErpSAStatusMapping.forEach(mapping => {
                const { externalId, entityId } = mapping;

                let selectItem;

                serviceAppointmentStatusesList.forEach(status => {
                    if (status.id === entityId) {
                        selectItem = status;
                    }
                });

                state[externalId] = { ...selectItem };
            });

            setServiceAppointmentSelectState({ ...serviceAppointmentSelectState, ...state });
        }
    }, [existingErpSAStatusMapping]);

    const sorter = (a, b, property = 'name') => {
        let roleA = a[property].toUpperCase();
        let roleB = b[property].toUpperCase();

        if (roleA < roleB) {
            return -1;
        }
        if (roleA > roleB) {
            return 1;
        }

        return 0;
    };

    const handleSave = () => {
        const results = [];
        const callMappings = [];
        const appointmentMappings = [];

        //Service Call Updates
        existingErpSCStatusMapping.forEach(mapping => {
            let isChanged = false;
            const target = serviceCallSelectState[mapping.externalId];

            isChanged = target.id && target.id !== mapping.entityId;

            if (isChanged) {
                callMappings.push({
                    id:         mapping.id,
                    externalId: mapping.externalId,
                    entityId:   target.id
                });
            }
        });

        //Service Call Creates
        Object.keys(serviceCallSelectState).forEach(key => {
            const isNotMapped = existingErpSCStatusMapping.find(map => map.externalId === key) === undefined;

            if (isNotMapped) {
                callMappings.push({
                    externalId: key,
                    entityId:   serviceCallSelectState[key].id
                });
            }
        });

        //Service Call Deletes
        Object.keys(serviceCallSelectState).forEach(key => {
            const isDeleted = serviceCallSelectState[key] === '';

            if (isDeleted) {
                const link = existingErpSCStatusMapping.find(map => map.externalId === key);
                callMappings.push({
                    linkId: link.id
                });
            }
        });

        //Service Appointment Updates
        existingErpSAStatusMapping.forEach(mapping => {
            let isChanged = false;
            const target = serviceAppointmentSelectState[mapping.externalId];

            isChanged = target.id && target.id !== mapping.entityId;

            if (isChanged) {
                appointmentMappings.push({
                    id:         mapping.id,
                    externalId: mapping.externalId,
                    entityId:   target.id
                });
            }
        });

        //Service Appointment Creates
        Object.keys(serviceAppointmentSelectState).forEach(key => {
            const isNotMapped = existingErpSAStatusMapping.find(map => map.externalId === key) === undefined;

            if (isNotMapped) {
                appointmentMappings.push({
                    externalId: key,
                    entityId:   serviceAppointmentSelectState[key].id
                });
            }
        });

        //Service Appointment Deletes
        Object.keys(serviceAppointmentSelectState).forEach(key => {
            const isDeleted = serviceAppointmentSelectState[key] === '';

            if (isDeleted) {
                const link = existingErpSAStatusMapping.find(map => map.externalId === key);
                appointmentMappings.push({
                    linkId: link.id
                });
            }
        });

        //Determines which mappings need to be created or updated
        callMappings.forEach(statusMap => {
            if (statusMap.id) {
                results.push(updateErpServiceCallMapping(statusMap.id, statusMap.entityId, statusMap.externalId, selectedErpAgentId, 'servicecallstatus'));
            } else if (statusMap.linkId) {
                results.push(deleteStatusMapping(statusMap.linkId));
            } else {
                results.push(createErpServiceCallMapping(statusMap.entityId, statusMap.externalId, selectedErpAgentId, 'servicecallstatus'));
            }
        });

        appointmentMappings.forEach(statusMap => {
            if (statusMap.id) {
                results.push(updateErpServiceCallMapping(statusMap.id, statusMap.entityId, statusMap.externalId, selectedErpAgentId, 'serviceappointmentstatus'));
            } else if (statusMap.linkId) {
                results.push(deleteStatusMapping(statusMap.linkId));
            } else {
                results.push(createErpServiceCallMapping(statusMap.entityId, statusMap.externalId, selectedErpAgentId, 'serviceappointmentstatus'));
            }
        });

        setIsLoading(true);

        Promise.all(results).then((data) => {
            if (results.length === 0) {
                enqueueSnackbar('No changes detected to ERP Service Call Status Mappings.', 'info');
            } else if (data.some(item => item === false)) {
                enqueueSnackbar('Some ERP Service Call Status Mappings failed to update.', 'error');
            } else {
                getMapping();
                enqueueSnackbar('ERP Service Call Status Mapping updated successfully.', 'success');
            }
            setIsLoading(false);
        });
    };

    const handleServiceCallStatusChange = (event, rowData) => {
        const state = { ...serviceCallSelectState };

        state[rowData.id] = event.target.value;
        setServiceCallSelectState(state);
    };

    const handleServiceAppointmentStatusChange = (event, rowData) => {
        const state = { ...serviceAppointmentSelectState };

        state[rowData.id] = event.target.value;
        setServiceAppointmentSelectState(state);
    };

    const handleReturnToIotHub = () => {
        const { companyInfo, history } = props;
        history.push(`/iothub?company=${companyInfo.companyId}`);
    };

    const noErpAgentsInstalledContent = () => {
        return (
            <Paper className={classes.serviceCallStatusMappingTable}>
                <div className={classes.noErpAgentsContainer}>
                    <img src="/assets/images/mixerLegoFlat.svg" alt="mixer icon" />
                    <h2>There are no ERP Agents installed!</h2>
                    <h2>Set one up in IoT Hub?</h2>
                    <Button
                        className={classes.paperButton}
                        variant="contained"
                        color="primary"
                        onClick={handleReturnToIotHub}
                    >
                        Whisk me away to IoT Hub!
                    </Button>
                </div>
            </Paper>
        );
    };

    const contentLoading = () => {
        return (
            <Paper className={classes.serviceCallStatusMappingTable}>
                <div className={classes.noErpAgentsContainer}>
                    <CircularProgress
                        size={50}
                    />
                </div>
            </Paper>
        );
    };

    const selectErpAgentContent = () => {
        return (
            <Paper className={classes.serviceCallStatusMappingTable}>
                <div className={classes.noErpAgentsContainer}>
                    <img src="/assets/images/explorerLegoFlat.svg" alt="explorer icon" />
                    <h2>Select an ERP Agent from the list to start mapping.</h2>
                </div>
            </Paper>
        );
    };

    const serviceCallAutoCompleteOptions = [
        {
            itemProps: {
                type: 'serviceCallStatus'
            },
            items: serviceCallStatusesList.map(status => ({
                id:   status.id,
                name: status.label
            })).sort(sorter),
            name: 'Call Statuses'
        }
    ];

    const serviceAppointmentAutoCompleteOptions = [
        {
            itemProps: {
                type: 'serviceAppointmentStatus'
            },
            items: serviceAppointmentStatusesList.map(status => ({
                id:   status.id,
                name: status.label
            })).sort(sorter),
            name: 'Appointment Statuses'
        }
    ];

    const renderAutoComplete = rowData => {
        if (rowData.type === 'servicecall') {
            return (
                <AutoComplete
                    grouped
                    name="serviceCallStatus"
                    label="Select Service Call Status"
                    onChange={e => handleServiceCallStatusChange(e, rowData)}
                    options={serviceCallAutoCompleteOptions}
                    value={serviceCallSelectState[rowData.id]}
                />
            );
        }

        if (rowData.type === 'serviceappointment') {
            return (
                <AutoComplete
                    grouped
                    name="serviceAppointmentStatus"
                    label="Service Appointment Status"
                    onChange={e => handleServiceAppointmentStatusChange(e, rowData)}
                    options={serviceAppointmentAutoCompleteOptions}
                    value={serviceAppointmentSelectState[rowData.id]}
                />
            );
        }
    };

    const renderType = rowData => {
        if (rowData.type === 'servicecall') {
            return 'Service Call';
        }
        if (rowData.type === 'serviceappointment') {
            return 'Service Appointment';
        }
        return rowData.type;
    };

    const typeSorter = (a, b) => {
        const valueA = a.type.toUpperCase();
        const valueB = b.type.toUpperCase();

        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }

        return 0;
    };

    const defaultSort = (erpExternalStatuses) => {
        return sortBy(erpExternalStatuses, ['type', 'label']);
    };

    const serviceCallStatusMappingTableContent = () => {
        return (
            <main className={classes.root}>
                <Paper className={classes.body}>
                    <div className={classes.bodyContent}>
                        <div className={classes.formColumn}>
                            <div className={classes.labelColumn}>
                                <div className={classes.labelHeader}>
                                    <img src="/assets/images/advisorServiceIcon.svg" alt="service call statuses" />
                                    <span>Map Service Call Statuses</span>
                                </div>
                                <div className={classes.labelContent}>
                                    <p>
                                        For the Building Optimization Broker to sync Service Calls with ERP systems, the ERP Statuses must be mapped to Optimization Broker Service Call Statuses.
                                    </p>
                                    <p>
                                        Any unmapped Statuses will be ignored.
                                    </p>
                                </div>
                            </div>
                            <div className={classes.inputColumn}>
                                <MaterialTable
                                    isLoading={isLoading}
                                    options={{
                                        paging:               false,
                                        showTitle:            false,
                                        showTextRowsSelected: false,
                                        search:               false,
                                        selection:            false,
                                        sorting:              true,
                                        toolbar:              false
                                    }}
                                    columns={[
                                        {
                                            title:      'Type',
                                            customSort: typeSorter,
                                            render:     rowData => renderType(rowData)

                                        },
                                        {
                                            field:   'label',
                                            title:   'ERP Status Name',
                                            sorting: false

                                        },
                                        {
                                            title:   'BOB Status',
                                            render:  rowdata => renderAutoComplete(rowdata),
                                            sorting: false
                                        }
                                    ]}
                                    data={defaultSort(selectedErpAgentStatuses)}
                                />
                            </div>
                        </div>
                        <div className={classes.cancelSaveContainer}>
                            <CancelButton
                                cancel={() => getMapping()}
                            />
                            <ConfirmButton
                                confirm={handleSave}
                                label="Save"
                                type="button"
                            />
                        </div>
                    </div>
                </Paper>
            </main>
        );
    };

    content = contentLoading();

    if (erpAgents.length === 0 && !erpAgentsLoading) {
        content = noErpAgentsInstalledContent();
    } else if (!isEmpty(selectedErpAgentId)) {
        content = serviceCallStatusMappingTableContent();
    } else if (isEmpty(selectedErpAgentId) && !erpAgentsLoading) {
        content = selectErpAgentContent();
    }

    if (!existingErpSCStatusMapping && !serviceCallSelectState && !serviceAppointmentSelectState) {
        return null;
    }

    return (
        <React.Fragment>
            {content}
        </React.Fragment>
    );
};

ServiceCallStatusMappingTable.defaultProps = {
    companyInfo:                {},
    existingErpSCStatusMapping: [],
    selectedErpAgentStatuses:   [],
    serviceCallStatusesList:    []
};

ServiceCallStatusMappingTable.propTypes = {
    companyInfo:                object,
    existingErpSCStatusMapping: array,
    selectedErpAgentStatuses:   array,
    serviceCallStatusesList:    array
};

const mapStateToProps = state => {
    return {
        companyInfo:                    state.companyInfo,
        erpAgents:                      state.erpAgent.erpAgents,
        erpAgentsLoading:               state.erpAgent.erpAgentsLoading,
        existingErpSCStatusMapping:     state.architect.selectedErpServiceCallMapping,
        existingErpSAStatusMapping:     state.architect.selectedErpServiceApptMapping,
        selectedErpAgentId:             state.architect.selectedErpAgentId,
        selectedErpAgentStatuses:       state.architect.selectedErpAgentStatuses,
        serviceAppointmentStatusesList: state.architect.serviceAppointmentStatusesList,
        serviceCallStatusesList:        state.architect.serviceCallStatusesList
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...ArchitectActions
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ServiceCallStatusMappingTable));
